// <purpose>The ONE decision behind the Formular-Excel button on the Antworten page: may a click act,
// what does the tooltip promise beforehand, and — if it may not act — which reason does the clerk get
// told. All three are one computation with one set of strings.</purpose>
// <dependencies>None. Pure; no React, no api, no theme — so it is testable as a table and cannot drift
// with rendering.</dependencies>
// <constraints>⚠ THE POINT OF THIS MODULE IS THAT `canAct` AND `refusal` CANNOT DISAGREE. Before it, the
// tooltip distinguished every refusal reason while the toast said one generic sentence for all of them,
// so a clerk blocked by a tenant lever or by the batch cap was told "no selection" — a statement about a
// thing they had done nothing wrong with. Measured live: the operator read that string, concluded the
// selection had been dropped, and clicked again. Keep `refusal === null` exactly when `canAct` is true;
// `RefusalAndActionAreOneDecision` asserts it over the whole table.
// ⚠ This page's TWO gestures are why the empty-pick case can still act: an explicit pick sends those
// uuids, an empty pick sends the VIEW and the server resolves it. The Archiv page deliberately has only
// the first gesture, so its plain "no selection" message is TRUE there — do not pull that page onto this
// helper, that would entrench a shape it does not have.</constraints>

export interface ExcelBatchActionInput {
  /** Tickets the clerk ticked. > 0 means the explicit-uuids gesture. */
  pickedCount: number;
  /** Server-side count of form-bearing tickets in the CURRENT bucket + filters. */
  formBearingCount: number;
  /** Server-supplied ceiling. Infinity when the backend never sent one — an older backend must not pre-refuse. */
  formBatchCap: number;
  /** Tenant lever FORM_EXTRACTION_ENABLED, read from the counts payload. */
  formExtractionOff: boolean;
  /** The first counts fetch has not resolved yet, so formBearingCount is not yet a fact. */
  countsLoading: boolean;
  /** The counts fetch FAILED. Distinct from loading: this one will not resolve on its own. */
  countsFailed: boolean;
}

export interface ExcelBatchAction {
  canAct: boolean;
  /** What the button promises BEFORE the click. */
  title: string;
  /** What the clerk is told when a click cannot act. Non-null exactly when canAct is false. */
  refusal: string | null;
}

export function describeExcelBatchAction(input: ExcelBatchActionInput): ExcelBatchAction {
  const { pickedCount, formBearingCount, formBatchCap, formExtractionOff, countsLoading, countsFailed } = input;

  // An explicit pick acts regardless of tenant lever or cap — those constrain the view-wide gesture,
  // not tickets the clerk has already singled out. This mirrors the shipped gate exactly.
  if (pickedCount > 0) {
    return {
      canAct: true,
      title: `Formularzeilen der Auswahl als Excel herunterladen (${pickedCount})`,
      refusal: null,
    };
  }

  // ⚠ BEFORE EVERY BRANCH THAT READS A COUNT AS A FACT. When the counts fetch failed, EVERY field of
  // the payload is absent — including the tenant lever, which then reads as "on" and the count as 0.
  // Falling through would produce "no ticket in this view has a recognised form": a confident factual
  // claim about data we failed to fetch. MEASURED by the pre-merge reviewer with a partial failure (one
  // bucket's fetch rejected, the other holding 5 deliverable tickets) — the clerk was refused with a
  // false measurement. This is the zero-by-initialisation defect wearing a second face: zero-by-failure.
  if (countsFailed) {
    const m = 'Die Anzahl der Vorgaenge mit Formular konnte nicht geladen werden. '
      + 'Bitte die Ansicht neu laden.';
    return { canAct: false, title: m, refusal: m };
  }

  if (formExtractionOff) {
    const m = 'Formularerkennung ist fuer diesen Mandanten nicht aktiviert.';
    return { canAct: false, title: m, refusal: m };
  }

  if (formBearingCount > formBatchCap) {
    const m = `Mehr als ${formBatchCap} Vorgaenge mit Formular. Bitte die Auswahl per Filter eingrenzen.`;
    return { canAct: false, title: m, refusal: m };
  }

  if (formBearingCount > 0) {
    // ⚠ DURING A REFETCH THE COUNT IS THE PREVIOUS VIEW'S. The click stays enabled — it sends the VIEW
    // and the server resolves the current filters, so the ACTION is right and refusing here would be a
    // regression. But the NUMBER is stale, so it is not asserted: the label drops "aller N" rather than
    // promising a figure this client cannot currently support. Same rule as everywhere else in this
    // module — never state a count you have not measured.
    return {
      canAct: true,
      title: countsLoading
        ? 'Formularzeilen der aktuellen Ansicht herunterladen'
        // "aller N" is truthful about the BUCKET, not about the page that happens to be loaded.
        : `Formularzeilen aller ${formBearingCount} Vorgaenge mit erkanntem Formular herunterladen`,
      refusal: null,
    };
  }

  // ⚠ ZERO AND NOT-YET-KNOWN ARE DIFFERENT ANSWERS AND MUST NOT SHARE A SENTENCE. Until the first
  // counts fetch resolves the count is 0 by initialisation, not by measurement, so "no ticket here has
  // a form" would be a claim the client cannot support — and this is the state the operator's first
  // click actually landed in.
  if (countsLoading) {
    const m = 'Die Anzahl der Vorgaenge mit Formular wird noch geladen. Bitte einen Augenblick warten.';
    return { canAct: false, title: m, refusal: m };
  }

  const m = 'Kein Vorgang in dieser Ansicht enthaelt ein erkanntes Formular.';
  return { canAct: false, title: m, refusal: m };
}
